import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { MapPinIcon, PackageIcon, ShapesIcon, WarehouseIcon } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { z } from 'zod'
import { useLocations } from '@/api/locations'
import { LOCATION_KINDS, SOURCE_TYPES, useMovements } from '@/api/stock'
import { useWorkspace } from '@/api/workspace'
import { CsvExportButton } from '@/components/csv-export/csv-export-button'
import { Chip, FilterBar } from '@/components/filter-bar'
import { movementItemLabel } from '@/components/stock/item-label'
import { MovementList } from '@/components/stock/movement-list'

// Movement history (UI-SCREENS I-03): the whole ledger, newest first in entry order, with the
// filters above it as a Linear-style bar of chips — the kind of location, one specific Location (Home or one Sales Channel),
// type and the date range. The two location filters combine: both apply, so picking a Location
// only narrows further (#176 Q2). Every filter lives in the URL, so a filtered list is linkable
// and survives a reload. `?itemId=` narrows to one stock item, so a row on M-03, I-01, I-02 or
// P-10 can open its history here; it shows as a chip that can be cleared.
//
// Owner and Staff both see it, values included (permissions.md Inventory, UI-SCREENS G2).
const searchSchema = z.object({
  itemId: z.coerce.number().int().positive().optional().catch(undefined),
  location: z.enum(LOCATION_KINDS).optional().catch(undefined),
  locationId: z.coerce.number().int().positive().optional().catch(undefined),
  type: z.enum(SOURCE_TYPES).optional().catch(undefined),
  from: z.string().optional().catch(undefined),
  to: z.string().optional().catch(undefined),
})

export const Route = createFileRoute('/w/$slug/stock/movements')({
  component: MovementHistory,
  validateSearch: searchSchema,
  staticData: { breadcrumb: 'stock.movements.title' },
})

function MovementHistory() {
  const { t } = useTranslation()
  const { slug } = Route.useParams()
  const search = Route.useSearch()
  const navigate = useNavigate({ from: Route.fullPath })
  const locations = useLocations(slug)
  const filters = {
    stockItemId: search.itemId,
    locationKind: search.location,
    locationId: search.locationId,
    sourceType: search.type,
    from: search.from,
    to: search.to,
  }
  // The first page is already in the cache from the list below; it names the filtered item.
  const page = useMovements(slug, filters, 1)
  const itemLabel = search.itemId ? (page.data?.items[0] && movementItemLabel(page.data.items[0].stock_item)) : undefined
  // The CSV is Owner only (permissions.md): hidden for Staff, and the endpoint refuses them. It
  // takes the same filters, so the file holds every page of the list on screen.
  const isOwner = useWorkspace(slug).data?.role === 'owner'
  const exportQuery = {
    stock_item_id: filters.stockItemId,
    location_kind: filters.locationKind,
    location_id: filters.locationId,
    source_type: filters.sourceType,
    from: filters.from,
    to: filters.to,
  }

  function set(patch: Partial<typeof search>) {
    navigate({ search: (current) => ({ ...current, ...patch }), replace: true })
  }

  return (
    <section className="flex flex-col gap-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="font-heading text-xl font-semibold">{t('stock.movements.title')}</h1>
        {isOwner ? <CsvExportButton slug={slug} list="movements" query={exportQuery} /> : null}
      </div>

      <FilterBar
        fields={[
          {
            key: 'location',
            label: t('stock.movements.filters.location'),
            icon: WarehouseIcon,
            value: search.location,
            options: LOCATION_KINDS.map((kind) => ({ value: kind, label: t(`stock.movements.locations.${kind}`) })),
            onChange: (value) => set({ location: value as (typeof LOCATION_KINDS)[number] | undefined }),
          },
          // One Location on top of the kind: Gudang, or one Sales Channel by name (#176).
          {
            key: 'locationId',
            label: t('stock.movements.filters.locationId'),
            icon: MapPinIcon,
            value: search.locationId ? String(search.locationId) : undefined,
            options: (locations.data ?? []).map((location) => ({
              value: String(location.id),
              label:
                (location.name ?? t(`stock.movements.locations.${location.kind}`)) +
                (location.archived ? ` (${t('stock.archived')})` : ''),
            })),
            onChange: (value) => set({ locationId: value === undefined ? undefined : Number(value) }),
          },
          {
            key: 'type',
            label: t('stock.movements.filters.sourceType'),
            icon: ShapesIcon,
            value: search.type,
            options: SOURCE_TYPES.map((type) => ({ value: type, label: t(`materials.movements.types.${type}`) })),
            onChange: (value) => set({ type: value as (typeof SOURCE_TYPES)[number] | undefined }),
          },
        ]}
        date={{ from: search.from, to: search.to }}
        onDateChange={(range) => set({ from: range.from, to: range.to })}
        extraActive={search.itemId !== undefined}
        onClearAll={() =>
          set({ itemId: undefined, location: undefined, locationId: undefined, type: undefined, from: undefined, to: undefined })
        }
      >
        {/* The stock item a row elsewhere opened this history for; it can only be cleared here. */}
        {search.itemId ? (
          <Chip icon={PackageIcon} label={t('stock.movements.item')} onRemove={() => set({ itemId: undefined })}>
            <span className="truncate px-2">{itemLabel ?? `#${search.itemId}`}</span>
          </Chip>
        ) : null}
      </FilterBar>

      {/* A new filter set is a new list: the key remounts it at page 1. */}
      <MovementList
        key={JSON.stringify(filters)}
        slug={slug}
        filters={filters}
        emptyMessage={Object.values(filters).some((value) => value !== undefined) ? t('stock.movements.emptyFiltered') : undefined}
      />
    </section>
  )
}
